import { useCallback, useRef, useState } from 'react'

import { useUploadFile } from '@/api/storage'
import { toaster } from '@/components/ui/toaster'

export type FileStatus = 'pending' | 'uploading' | 'done' | 'error'

export interface FileEntry {
  id: string
  file: File
  status: FileStatus
  imageUrl?: string
  uploaded: boolean
}

interface FileCount {
  total: number
  completed: number
  error: number
}

interface FileFlags {
  hasFiles: boolean
  allDone: boolean
  isUploading: boolean
}

interface UseMultiFileUploadOptions {
  maxFiles?: number
  concurrency?: number
}

const DEFAULT_MAX_FILES = 300
const DEFAULT_CONCURRENCY = 8

export default function useMultiFileUpload({
  maxFiles = DEFAULT_MAX_FILES,
  concurrency = DEFAULT_CONCURRENCY
}: UseMultiFileUploadOptions = {}) {
  const [files, setFiles] = useState<FileEntry[]>([])
  const abortRef = useRef(false)

  const uploadFile = useUploadFile()

  const updateFile = useCallback((id: string, updates: Partial<FileEntry>) => {
    setFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...updates } : f))
    )
  }, [])

  const processUploads = useCallback(
    async (pending: FileEntry[]) => {
      if (!pending.length) return

      abortRef.current = false
      let index = 0

      const uploadNext = async (): Promise<void> => {
        while (index < pending.length) {
          if (abortRef.current) return

          const entry = pending[index]
          index++

          updateFile(entry.id, { status: 'uploading' })

          try {
            const imageUrl = await uploadFile.mutateAsync(entry.file)
            updateFile(entry.id, {
              status: 'done',
              imageUrl,
              uploaded: true
            })
          } catch {
            updateFile(entry.id, { status: 'error' })
          }
        }
      }

      const workers = Array.from(
        { length: Math.min(concurrency, pending.length) },
        () => uploadNext()
      )

      await Promise.all(workers)
    },
    [concurrency, uploadFile, updateFile]
  )

  const addFiles = useCallback(
    (newFiles: File[]) => {
      let entries: FileEntry[] = []
      let showMaxWarning = false
      let trimmedCount = 0
      let originalCount = 0

      setFiles((prev) => {
        const remainingSlots = maxFiles - prev.length

        if (remainingSlots <= 0) {
          showMaxWarning = true
          return prev
        }

        const filesToAdd = newFiles.slice(0, remainingSlots)
        trimmedCount = filesToAdd.length
        originalCount = newFiles.length

        entries = filesToAdd.map((file) => ({
          id: crypto.randomUUID(),
          file,
          status: 'pending' as const,
          uploaded: false
        }))

        return [...prev, ...entries]
      })

      if (showMaxWarning) {
        toaster.create({
          title: `Maximum ${maxFiles} files allowed`,
          type: 'warning'
        })
        return
      }

      if (originalCount > trimmedCount) {
        toaster.create({
          title: `Only ${trimmedCount} of ${originalCount} files added (limit: ${maxFiles})`,
          type: 'warning'
        })
      }

      if (entries.length) {
        processUploads(entries)
      }
    },
    [maxFiles, processUploads]
  )

  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId))
  }, [])

  const retryFile = useCallback(
    (fileId: string) => {
      let retriedFile: FileEntry | null = null
      setFiles((prev) => {
        const file = prev.find((f) => f.id === fileId)
        if (!file || file.status !== 'error') return prev
        const updatedFile: FileEntry = { ...file, status: 'pending' as const }
        retriedFile = updatedFile
        const updated = prev.map((f) => (f.id === fileId ? updatedFile : f))
        return updated
      })
      if (retriedFile) {
        processUploads([retriedFile])
      }
    },
    [processUploads]
  )

  const reset = useCallback(() => {
    setFiles([])
    abortRef.current = true
  }, [])

  const abort = useCallback(() => {
    abortRef.current = true
  }, [])

  const count: FileCount = {
    total: files.length,
    completed: files.filter((f) => f.status === 'done').length,
    error: files.filter((f) => f.status === 'error').length
  }

  const flags: FileFlags = {
    hasFiles: count.total > 0,
    allDone:
      count.total > 0 &&
      files.every((f) => f.status === 'done' || f.status === 'error'),
    isUploading: files.some((f) => f.status === 'uploading')
  }

  return {
    files,
    count,
    flags,
    addFiles,
    removeFile,
    retryFile,
    reset,
    abort
  }
}
